/**
 * Admin → Guardrails config (§27.8).
 *
 * Edits the account-wide Bedrock guardrail (terraform/guardrails.tf ->
 * content_filter). GET loads the live DRAFT config; Save invokes
 * UpdateGuardrail and publishes a numbered version. Note: runtime
 * Lambdas pin to `runtimeVersion` from Terraform and won't pick up
 * changes without a redeploy — the UI surfaces this explicitly.
 */
import { useEffect, useState } from "react";
import {
  getAdminGuardrail,
  updateAdminGuardrail,
} from "../services/admin.js";
import ErrorBubble from "../components/ErrorBubble.jsx";
import LoadingSkeleton from "../components/LoadingSkeleton.jsx";

const CONTENT_FILTER_TYPES = [
  "SEXUAL",
  "VIOLENCE",
  "HATE",
  "INSULTS",
  "MISCONDUCT",
  "PROMPT_ATTACK",
];
const STRENGTHS = ["NONE", "LOW", "MEDIUM", "HIGH"];
const PII_TYPES = [
  "EMAIL",
  "PHONE",
  "NAME",
  "ADDRESS",
  "CREDIT_DEBIT_CARD_NUMBER",
  "US_SOCIAL_SECURITY_NUMBER",
  "IP_ADDRESS",
  "URL",
  "USERNAME",
  "PASSWORD",
];
const PII_ACTIONS = ["ANONYMIZE", "BLOCK", "NONE"];

function filterByType(filters, type) {
  return filters.find((f) => f.type === type);
}

function piiByType(entities, type) {
  return entities.find((e) => e.type === type);
}

export default function AdminGuardrails() {
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [saveError, setSaveError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(null);

  const [meta, setMeta] = useState(null);
  const [contentFilters, setContentFilters] = useState([]);
  const [piiEntities, setPiiEntities] = useState([]);
  const [deniedTopicsText, setDeniedTopicsText] = useState("");
  const [deniedWordsText, setDeniedWordsText] = useState("");

  useEffect(() => {
    let cancelled = false;
    getAdminGuardrail()
      .then((data) => {
        if (cancelled) return;
        hydrate(data);
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        setLoadError(err.message);
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  function hydrate(data) {
    const g = data.guardrail || {};
    setMeta({
      name: g.name,
      guardrailId: data.guardrailId,
      runtimeVersion: data.runtimeVersion,
    });
    setContentFilters(
      CONTENT_FILTER_TYPES.map((type) => {
        const existing = filterByType(g.contentFilters || [], type) || {};
        return {
          type,
          inputStrength: existing.inputStrength || "NONE",
          outputStrength: existing.outputStrength || "NONE",
        };
      }),
    );
    setPiiEntities(
      (g.piiEntities || []).map((e) => ({ type: e.type, action: e.action })),
    );
    setDeniedTopicsText(
      (g.deniedTopics || [])
        .map((t) => `${t.name}: ${t.definition}`)
        .join("\n"),
    );
    setDeniedWordsText((g.deniedWords || []).join("\n"));
  }

  function updateContentFilter(type, field, value) {
    setContentFilters((prev) =>
      prev.map((f) => (f.type === type ? { ...f, [field]: value } : f)),
    );
    setSaved(null);
  }

  function addPiiEntity() {
    const unused = PII_TYPES.find((t) => !piiByType(piiEntities, t));
    if (!unused) return;
    setPiiEntities((prev) => [...prev, { type: unused, action: "ANONYMIZE" }]);
    setSaved(null);
  }

  function removePiiEntity(type) {
    setPiiEntities((prev) => prev.filter((e) => e.type !== type));
    setSaved(null);
  }

  function updatePiiEntity(idx, field, value) {
    setPiiEntities((prev) =>
      prev.map((e, i) => (i === idx ? { ...e, [field]: value } : e)),
    );
    setSaved(null);
  }

  function parseDeniedTopics(text) {
    return text
      .split(/\r?\n/)
      .map((line) => line.trim())
      .filter(Boolean)
      .map((line) => {
        const sep = line.indexOf(":");
        if (sep === -1) return null;
        const name = line.slice(0, sep).trim();
        const definition = line.slice(sep + 1).trim();
        if (!name || !definition) return null;
        return { name, definition, examples: [] };
      })
      .filter(Boolean);
  }

  function parseDeniedWords(text) {
    return text
      .split(/\r?\n/)
      .map((w) => w.trim())
      .filter(Boolean);
  }

  async function handleSave() {
    setSaveError(null);
    setSaved(null);
    setSaving(true);
    try {
      const payload = {
        contentFilters,
        piiEntities,
        deniedTopics: parseDeniedTopics(deniedTopicsText),
        deniedWords: parseDeniedWords(deniedWordsText),
      };
      const resp = await updateAdminGuardrail(payload);
      hydrate(resp);
      setSaved(resp.publishedVersion);
    } catch (err) {
      setSaveError(err.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="admin-page">
      <div className="admin-page__header">
        <h1>Guardrails</h1>
      </div>

      {loadError && <ErrorBubble message={loadError} />}
      {saveError && <ErrorBubble message={saveError} />}

      {loading ? (
        <LoadingSkeleton variant="line" />
      ) : (
        <>
          {meta && (
            <p className="admin-page__hint">
              Editing <code>{meta.name}</code> ({meta.guardrailId}). Runtime
              Lambdas are pinned to version{" "}
              <strong>{meta.runtimeVersion}</strong> — saved changes publish a
              new version but won&apos;t take effect until Terraform re-pins
              the chat and pipeline-worker Lambdas.
            </p>
          )}

          <section className="admin-guardrails__section">
            <h2>Content filters</h2>
            <p className="admin-page__hint">
              Strength controls how aggressively each category is filtered on
              user input and model output.
            </p>
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Category</th>
                  <th>Input strength</th>
                  <th>Output strength</th>
                </tr>
              </thead>
              <tbody>
                {contentFilters.map((f) => (
                  <tr key={f.type}>
                    <td>{f.type}</td>
                    <td>
                      <select
                        value={f.inputStrength}
                        onChange={(e) =>
                          updateContentFilter(
                            f.type,
                            "inputStrength",
                            e.target.value,
                          )
                        }
                      >
                        {STRENGTHS.map((s) => (
                          <option key={s} value={s}>
                            {s}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td>
                      <select
                        value={f.outputStrength}
                        onChange={(e) =>
                          updateContentFilter(
                            f.type,
                            "outputStrength",
                            e.target.value,
                          )
                        }
                      >
                        {STRENGTHS.map((s) => (
                          <option key={s} value={s}>
                            {s}
                          </option>
                        ))}
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>

          <section className="admin-guardrails__section">
            <h2>PII handling</h2>
            <p className="admin-page__hint">
              ANONYMIZE masks the entity in model output; BLOCK rejects the
              turn; NONE disables the rule.
            </p>
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Entity</th>
                  <th>Action</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {piiEntities.map((e, idx) => (
                  <tr key={e.type}>
                    <td>
                      <select
                        value={e.type}
                        onChange={(ev) =>
                          updatePiiEntity(idx, "type", ev.target.value)
                        }
                      >
                        {PII_TYPES.map((t) => (
                          <option
                            key={t}
                            value={t}
                            disabled={
                              t !== e.type && !!piiByType(piiEntities, t)
                            }
                          >
                            {t}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td>
                      <select
                        value={e.action}
                        onChange={(ev) =>
                          updatePiiEntity(idx, "action", ev.target.value)
                        }
                      >
                        {PII_ACTIONS.map((a) => (
                          <option key={a} value={a}>
                            {a}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td>
                      <button
                        type="button"
                        className="btn btn-ghost"
                        onClick={() => removePiiEntity(e.type)}
                      >
                        Remove
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <button
              type="button"
              className="btn btn-ghost"
              onClick={addPiiEntity}
              disabled={piiEntities.length >= PII_TYPES.length}
            >
              + Add PII rule
            </button>
          </section>

          <section className="admin-guardrails__section">
            <h2>Denied topics</h2>
            <p className="admin-page__hint">
              One per line, formatted as <code>Name: Definition</code>. Bedrock
              rejects prompts or responses that fit a denied topic&apos;s
              definition.
            </p>
            <textarea
              className="admin-json-editor"
              rows={6}
              value={deniedTopicsText}
              onChange={(e) => {
                setDeniedTopicsText(e.target.value);
                setSaved(null);
              }}
              placeholder="Competitor pricing: Discussion of competitor pricing strategy."
            />
          </section>

          <section className="admin-guardrails__section">
            <h2>Denied words</h2>
            <p className="admin-page__hint">
              One per line. Exact-match blocklist — use for internal codenames
              or terms the model should never echo.
            </p>
            <textarea
              className="admin-json-editor"
              rows={6}
              value={deniedWordsText}
              onChange={(e) => {
                setDeniedWordsText(e.target.value);
                setSaved(null);
              }}
              placeholder="project-thunderbird"
            />
          </section>

          <div className="admin-page__actions">
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? "Saving…" : "Save"}
            </button>
            {saved && (
              <span className="admin-page__saved">
                Saved · published version {saved}
              </span>
            )}
          </div>
        </>
      )}
    </div>
  );
}
